import { Badge, Button, useToast } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { useSharedProjectFiles } from '../../api/hooks';
import { api } from '../../lib/api';
import type { SharedProjectFile } from '../../api/schemas';

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} kB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
}

/** „David Harant" → „DH"; jednoslovné → první písmeno. */
function initials(name?: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

async function downloadShared(file: SharedProjectFile) {
  const blob = await api.get<Blob>(`/project-docs/shared-files/${file.file_id}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename || file.nazev || 'soubor';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Otevře nasdílený soubor v nové záložce (náhled). */
async function openShared(file: SharedProjectFile) {
  const w = window.open('', '_blank');
  try {
    const blob = await api.get<Blob>(`/project-docs/shared-files/${file.file_id}`);
    const url = URL.createObjectURL(blob);
    if (w) w.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}

/**
 * Dokumenty, které přihlášenému uživateli někdo nasdílel jednotlivě.
 * Route: /app/dokumenty-projektu/nasdilene
 */
export function NasdileneDokumentyPage() {
  const { data: items = [], isLoading, error } = useSharedProjectFiles();
  const toast = useToast();

  const columns: ListColumn<SharedProjectFile>[] = [
    {
      key: 'nazev',
      label: 'Soubor',
      primary: true,
      width: 'minmax(160px, 4fr)',
      render: (f) => (
        <button
          type="button"
          onClick={() => openShared(f).catch(() => toast.error('Otevření se nezdařilo'))}
          title="Otevřít v okně"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'inherit', font: 'inherit', textAlign: 'left', textDecoration: 'underline',
            textDecorationColor: 'var(--sd-border, var(--sd-w-d8d4c5, #d8d4c5))', textUnderlineOffset: 3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
          }}
        >
          {f.nazev || f.filename || '—'}
        </button>
      ),
    },
    { key: 'stavba_nazev', label: 'Stavba', width: '140px', render: (f) => f.stavba_nazev || '—' },
    {
      key: 'kategorie',
      label: 'Kategorie',
      width: '92px',
      render: (f) => <span style={{ fontSize: 10.5 }}><Badge tone="neutral">{f.kategorie || 'Ostatní'}</Badge></span>,
    },
    {
      key: 'owner_name',
      label: 'Sdílel',
      width: '56px',
      dim: true,
      render: (f) => <span title={f.owner_name || ''}>{initials(f.owner_name)}</span>,
    },
    { key: 'created_at', label: 'Nasdíleno', width: '84px', mono: true, dim: true, render: (f) => <span style={{ fontSize: 11 }}>{formatDate(f.created_at)}</span> },
    { key: 'size', label: 'Velikost', width: '78px', mono: true, dim: true, align: 'right', render: (f) => <span style={{ fontSize: 11 }}>{formatSize(f.size)}</span> },
  ];

  return (
    <ListPage
      breadcrumb={['Realizace', 'Nasdílené dokumenty']}
      title="Nasdílené dokumenty"
      subtitle={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} souborů`}
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} souborů`}
      searchPlaceholder="Hledat v názvu nebo stavbě…"
    >
      {error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <ListTable
          columns={columns}
          rows={items}
          rowKey={(f) => f.file_id}
          fluid
          rowActions={(f) => (
            <Button
              size="sm"
              variant="ghost"
              title="Stáhnout"
              onClick={() => downloadShared(f).catch(() => toast.error('Stažení se nezdařilo'))}
            >
              ⬇
            </Button>
          )}
          emptyTitle="Žádné nasdílené dokumenty"
          emptyText="Když vám někdo nasdílí dokument, objeví se zde."
        />
      )}
    </ListPage>
  );
}
